import { useTenantUsageStore } from "@/application/stores/tenantUsageStore";

type ResourceKey = "warehouses" | "products" | "users" | "sectionsByWarehouse";

/**
 * Hook to access tenant plan usage and limits.
 * Returns null-safe helpers — if data is not loaded, everything is "allowed".
 */
export function useTenantUsage() {
  const data = useTenantUsageStore(s => s.data);
  const refresh = useTenantUsageStore(s => s.refresh);

  /** Returns true if the resource has reached its plan limit */
  const isAtLimit = (key: ResourceKey): boolean => {
    if (!data?.limits) return false;
    const limit = data.limits[key];
    if (limit === -1) return false; // unlimited
    return data.usage[key] >= limit;
  };

  /** Returns true if a feature flag is enabled in the plan */
  const isFeatureEnabled = (feature: "bulkImport" | "qrBarcode" | "reports"): boolean => {
    if (!data?.limits) return true; // default to enabled if no data
    if (feature === "bulkImport") return data.limits.bulkImportEnabled;
    if (feature === "qrBarcode") return data.limits.qrBarcodeEnabled;
    if (feature === "reports") return data.limits.reportsEnabled ?? true;
    return true;
  };

  /** Returns the max reports per month, or null if unlimited */
  const maxReportsPerMonth = data?.limits?.maxReportsPerMonth ?? null;

  /** Returns the allowed analytics modules array, or null if no data */
  const analyticsModules = data?.limits?.analytics?.modules ?? null;

  /** Returns true if a specific analytics module is available */
  const isAnalyticsModuleEnabled = (module: string): boolean => {
    if (!data?.limits?.analytics?.modules) return true; // default to enabled
    return data.limits.analytics.modules.includes(module);
  };

  return {
    data,
    refresh,
    isAtLimit,
    isFeatureEnabled,
    analyticsModules,
    isAnalyticsModuleEnabled,
    maxReportsPerMonth,
  };
}
